import { ChangeEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { FieldErrors, UseFormSetValue } from "react-hook-form";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import {
	LinkedinPersonSuggestion,
	LinkedinPersonSuggestionItem,
} from "@/components/LinkedinPersonSuggestion/LinkedinPersonSuggestion";
import {
	LinkedinOrganizationSuggestion,
	LinkedinOrganizationSuggestionItem,
} from "@/components/LinkedinOrganizationSuggestion/LinkedinOrganizationSuggestion";

import { CreateLiPost } from "../schema/liPost";
import { LinkedinParams, LinkedinTagAutocompleteSingle } from "./LinkedinTagAutocomplete";
import { LinkedinSuggestions } from "./useLinkedinSuggestions";

interface TopicPostFiltersSectionProps {
	formValues: Partial<CreateLiPost>;
	setValue: UseFormSetValue<CreateLiPost>;
	errors: FieldErrors<CreateLiPost>;
	suggestions: LinkedinSuggestions;
	additionalColumns: boolean;
	onToggleAdditionalColumns: () => void;
}

/** BY_TOPIC optional post filters (author job/industry, mentions) behind a toggle. */
export const TopicPostFiltersSection: FunctionComponent<TopicPostFiltersSectionProps> = ({
	formValues,
	setValue,
	errors,
	suggestions,
	additionalColumns,
	onToggleAdditionalColumns,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liPost.${key}`);
	const {
		personSuggestions,
		setPersonSuggestions,
		setSearchValue,
		isLoadingPeople,
		organizationSuggestions,
		setOrganizationSuggestions,
		setCompanySearchValue,
	} = suggestions;

	const isUrlLocked = formValues?.postUrl !== "";

	return (
		<>
			<button
				className="text-ssm font-bold mb-8 flex items-center gap-2"
				onClick={onToggleAdditionalColumns}
			>
				Apply post filters <Icon icon={additionalColumns ? faChevronUp : faChevronDown} />
			</button>

			{additionalColumns && (
				<div className="mb-8">
					<div className="flex flex-row w-full mb-5">
						<InputField
							error={!!errors.authorJobTitle?.message}
							handleChange={(event: ChangeEvent<HTMLInputElement>) => {
								setValue("authorJobTitle", event.target.value, {
									shouldDirty: true,
									shouldValidate: true,
								});
							}}
							isDisabled={isUrlLocked}
							isOptional={true}
							label={ts("authorJobTitle")}
							name={"authorJobTitle"}
							placeholder={ts("authorJobTitlePlaceholder")}
							showError={true}
							value={formValues?.authorJobTitle || ""}
						/>
					</div>

					<div className="flex flex-col w-full mb-8">
						<label className="text-sm mb-2">{ts("authorIndustry")} (Optional)</label>
						<LinkedinTagAutocompleteSingle
							handleValueChange={(value: LinkedinParams | null) => {
								if (value) {
									setValue("authorIndustry", value, {
										shouldDirty: true,
										shouldValidate: true,
									});
								} else {
									setValue("authorIndustry", null);
								}
							}}
							isDisabled={isUrlLocked}
							type={"industry"}
							value={(formValues.authorIndustry as LinkedinParams) || null}
						/>
					</div>

					<div className="flex flex-col w-full mb-8">
						<label className="text-sm mb-2">Mentions Linkedin member (Optional)</label>
						<LinkedinPersonSuggestion
							data={personSuggestions || []}
							floating={true}
							handleSearch={(val) => setSearchValue(val)}
							handleSelect={(value?: LinkedinPersonSuggestionItem) => {
								if (value) {
									setValue("mentionsMember", value, {
										shouldDirty: true,
										shouldValidate: true,
									});

									setPersonSuggestions([]);
									setSearchValue("");
								} else {
									setValue("mentionsMember", null, {
										shouldDirty: true,
										shouldValidate: true,
									});
								}
							}}
							isDisabled={isUrlLocked}
							isLoading={isLoadingPeople}
							selectedItem={
								(formValues.mentionsMember as LinkedinPersonSuggestionItem) || undefined
							}
						/>
					</div>

					<div className="flex flex-col w-full mb-8">
						<label className="text-sm mb-2">Mentions Linkedin organization (Optional)</label>
						<LinkedinOrganizationSuggestion
							data={organizationSuggestions || []}
							floating={true}
							handleSearch={(val) => setCompanySearchValue(val)}
							handleSelect={(value?: LinkedinOrganizationSuggestionItem) => {
								if (value) {
									setValue("mentionsOrganization", value, {
										shouldDirty: true,
										shouldValidate: true,
									});

									setOrganizationSuggestions([]);
									setCompanySearchValue("");
								} else {
									setValue("mentionsOrganization", null, {
										shouldDirty: true,
										shouldValidate: true,
									});
								}
							}}
							isDisabled={isUrlLocked}
							selectedItem={
								(formValues.mentionsOrganization as LinkedinOrganizationSuggestionItem) || undefined
							}
						/>
					</div>
				</div>
			)}
		</>
	);
};
